/**
 * Markets page data: insights (changes, signals, volume, sentiment), sparklines per period, watchlists,
 * saved views and columns. Insights refresh every 30 s while the page is open; prices stay live from
 * the tickers channel.
 */
import { useEffect } from 'react';
import type { CalendarUpdate, EconCalendar, MarketBrief, MarketInsights, MarketPeriod, MarketPrefs, MarketSparks, PortfolioCorrelation, Watchlist } from '@platform/shared';
import { api, onTokenChange } from '../lib/api';
import { mergeCalendar } from '../lib/calendar-live';
import { createStore, useStore } from '../lib/store';
import { ws, wsState } from '../lib/ws';

interface MarketsState {
  insights: MarketInsights | null;
  calendar: EconCalendar | null;
  brief: MarketBrief | null;
  correlation: { accountId: string; data: PortfolioCorrelation } | null;
  sparks: Partial<Record<MarketPeriod, MarketSparks>>;
  watchlists: Watchlist[] | null;
  prefs: MarketPrefs | null;
}

export const marketsStore = createStore<MarketsState>({ insights: null, calendar: null, brief: null, correlation: null, sparks: {}, watchlists: null, prefs: null });

// Signing out (or in as someone else) drops the previous client's lists and views.
let lastToken: string | null = null;
onTokenChange((t) => {
  if (!t && lastToken) {
    marketsStore.set({ watchlists: null, prefs: null, correlation: null });
    loadingLists = null;
  }
  lastToken = t;
});

export async function loadInsights() {
  try {
    marketsStore.set({ insights: await api.get<MarketInsights>('/api/markets/insights') });
  } catch {
    /* keep the last insights */
  }
}

const sparksAt: Partial<Record<MarketPeriod, number>> = {};
export async function loadSparks(period: MarketPeriod, force = false) {
  if (!force && sparksAt[period] && Date.now() - sparksAt[period]! < 60_000) return;
  sparksAt[period] = Date.now();
  try {
    const s = await api.get<MarketSparks>(`/api/markets/sparks?period=${period}`);
    marketsStore.set((st) => ({ sparks: { ...st.sparks, [period]: s } }));
  } catch {
    delete sparksAt[period];
  }
}

/** Keep insights and the current period's sparklines fresh while mounted. */
export function useMarketsData(period: MarketPeriod) {
  useEffect(() => {
    void loadInsights();
    const t = window.setInterval(loadInsights, 30_000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    void loadSparks(period);
    const t = window.setInterval(() => loadSparks(period, true), 60_000);
    return () => window.clearInterval(t);
  }, [period]);
}

export const useInsights = () => useStore(marketsStore, (s) => s.insights);
export const useSparksFor = (period: MarketPeriod) => useStore(marketsStore, (s) => s.sparks[period]?.data ?? null);

// ---- watchlists ----

const LOCAL_FAVORITES = 'favorites';
const MIGRATED = 'favorites-migrated';

let loadingLists: Promise<void> | null = null;
export function loadWatchlists(force = false): Promise<void> {
  if (loadingLists && !force) return loadingLists;
  loadingLists = (async () => {
    try {
      let lists = await api.get<Watchlist[]>('/api/watchlists');
      // One-time move of the favourites this browser kept before watchlists lived on the server.
      let local: string[] = [];
      try {
        if (!localStorage.getItem(MIGRATED)) local = JSON.parse(localStorage.getItem(LOCAL_FAVORITES) ?? '[]');
      } catch {
        local = [];
      }
      if (Array.isArray(local) && local.length && lists[0]) {
        const merged = [...new Set([...lists[0].symbols, ...local.filter((x) => typeof x === 'string')])];
        const w = await api.patch<Watchlist>(`/api/watchlists/${lists[0].id}`, { symbols: merged });
        lists = [w, ...lists.slice(1)];
      }
      try {
        localStorage.setItem(MIGRATED, '1');
      } catch {
        /* storage unavailable */
      }
      marketsStore.set({ watchlists: lists });
    } catch {
      loadingLists = null;
    }
  })();
  return loadingLists;
}

function putList(w: Watchlist) {
  marketsStore.set((s) => ({ watchlists: (s.watchlists ?? []).map((x) => (x.id === w.id ? w : x)) }));
}

export async function createWatchlist(name: string, symbols: string[] = []) {
  const w = await api.post<Watchlist>('/api/watchlists', { name, symbols });
  marketsStore.set((s) => ({ watchlists: [...(s.watchlists ?? []), w] }));
  return w;
}

export async function renameWatchlist(id: string, name: string) {
  putList(await api.patch<Watchlist>(`/api/watchlists/${id}`, { name }));
}

export async function deleteWatchlist(id: string) {
  await api.del(`/api/watchlists/${id}`);
  marketsStore.set((s) => ({ watchlists: (s.watchlists ?? []).filter((x) => x.id !== id) }));
}

/** Add or remove one symbol (optimistic). */
export async function toggleInWatchlist(id: string, symbol: string) {
  const w = marketsStore.get().watchlists?.find((x) => x.id === id);
  if (!w) return;
  const has = w.symbols.includes(symbol);
  putList({ ...w, symbols: has ? w.symbols.filter((s) => s !== symbol) : [...w.symbols, symbol] });
  try {
    putList(await api.patch<Watchlist>(`/api/watchlists/${id}`, has ? { remove: symbol } : { add: symbol }));
  } catch {
    putList(w);
  }
}

export const useWatchlists = () => useStore(marketsStore, (s) => s.watchlists);

// ---- saved views and columns ----

export async function loadPrefs() {
  try {
    marketsStore.set({ prefs: await api.get<MarketPrefs>('/api/markets/prefs') });
  } catch {
    marketsStore.set({ prefs: {} });
  }
}

export async function savePrefs(patch: MarketPrefs) {
  marketsStore.set((s) => ({ prefs: { ...(s.prefs ?? {}), ...patch } }));
  try {
    marketsStore.set({ prefs: await api.put<MarketPrefs>('/api/markets/prefs', patch) });
  } catch {
    /* kept locally for this visit */
  }
}

export const usePrefs = () => useStore(marketsStore, (s) => s.prefs);

// ---- calendar, brief, correlation ----

let calendarAt = 0;
let following = false;
/**
 * The calendar for the Markets card, the trade-screen chip and the alerts page: loaded once, then kept live by
 * the WebSocket `calendar` channel (results arrive within seconds). Loaded again after a missed update, after a
 * reconnect, and every 15 minutes while the connection is down. An unchanged copy costs a 304.
 */
export async function loadCalendar(force = false) {
  if (!following) {
    following = true;
    ws.subscribe('calendar', (u: CalendarUpdate) => {
      const cal = marketsStore.get().calendar;
      if (!cal) return;
      const next = mergeCalendar(cal, u);
      if (next === null) void loadCalendar(true);
      else if (next !== cal) marketsStore.set({ calendar: next });
    });
    let was = wsState.get().status;
    wsState.subscribe(() => {
      const now = wsState.get().status;
      if (now === 'live' && was !== 'live') void loadCalendar(true);
      was = now;
    });
    window.setInterval(() => {
      if (wsState.get().status !== 'live') void loadCalendar(true);
    }, 15 * 60_000);
  }
  if (!force && Date.now() - calendarAt < 60_000) return;
  calendarAt = Date.now();
  try {
    marketsStore.set({ calendar: await api.get<EconCalendar>('/api/markets/calendar') });
  } catch {
    calendarAt = 0;
  }
}

let briefAt = 0;
export async function loadBrief() {
  if (Date.now() - briefAt < 30 * 60_000) return;
  briefAt = Date.now();
  try {
    marketsStore.set({ brief: await api.get<MarketBrief>('/api/markets/brief') });
  } catch {
    briefAt = 0;
  }
}

export async function loadCorrelation(accountId: string) {
  try {
    marketsStore.set({ correlation: { accountId, data: await api.get<PortfolioCorrelation>(`/api/markets/correlation?accountId=${accountId}`) } });
  } catch {
    /* keep */
  }
}

export const useCalendar = () => useStore(marketsStore, (s) => s.calendar);
export const useBrief = () => useStore(marketsStore, (s) => s.brief);
export const useCorrelation = () => useStore(marketsStore, (s) => s.correlation);
